import { useState, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Vehicle, HandlingRevision, VehicleClass } from '../types';
import { CLASS_COLOURS, PROGRESS_COLOURS, classLabel } from '../types';
import { vehiclesApi } from '../api/vehicles';
import { vehicleHandlingApi } from '../api/vehicleHandling';
import { vehicleImageSrc, formatDate } from '../utils/helpers';
import XmlHighlight from '../components/XmlHighlight';

// ─── Handling XML templates ──────────────────────────────────────────────────

const CAR_TEMPLATE = `    <Item type="CHandlingData">
      <handlingName>EXAMPLE</handlingName>
      <fMass value="0.000000"/>
      <fInitialDragCoeff value="0.000000"/>
      <fDownforceModifier value="0.000000"/>
      <fPopUpLightRotation value="0.000000"/>
      <fPercentSubmerged value="0.000000"/>
      <vecCentreOfMassOffset x="0.000000" y="0.000000" z="0.000000"/>
      <vecInertiaMultiplier x="0.000000" y="0.000000" z="0.000000"/>
      <fDriveBiasFront value="0.000000"/>
      <nInitialDriveGears value="0"/>
      <fInitialDriveForce value="0.000000"/>
      <fDriveInertia value="0.000000"/>
      <fClutchChangeRateScaleUpShift value="0.000000"/>
      <fClutchChangeRateScaleDownShift value="0.000000"/>
      <fInitialDriveMaxFlatVel value="0.000000"/>
      <fBrakeForce value="0.000000"/>
      <fBrakeBiasFront value="0.000000"/>
      <fHandBrakeForce value="0.000000"/>
      <fSteeringLock value="0.000000"/>
      <fTractionCurveMin value="0.000000"/>
      <fTractionCurveMax value="0.000000"/>
      <fTractionCurveLateral value="0.000000"/>
      <fTractionSpringDeltaMax value="0.000000"/>
      <fLowSpeedTractionLossMult value="0.000000"/>
      <fCamberStiffnesss value="0.000000"/>
      <fTractionBiasFront value="0.000000"/>
      <fTractionLossMult value="0.000000"/>
      <fSuspensionForce value="0.000000"/>
      <fSuspensionCompDamp value="0.000000"/>
      <fSuspensionReboundDamp value="0.000000"/>
      <fSuspensionUpperLimit value="0.000000"/>
      <fSuspensionLowerLimit value="0.000000"/>
      <fSuspensionRaise value="0.000000"/>
      <fSuspensionBiasFront value="0.000000"/>
      <fAntiRollBarForce value="0.000000"/>
      <fAntiRollBarBiasFront value="0.000000"/>
      <fRollCentreHeightFront value="0.000000"/>
      <fRollCentreHeightRear value="0.000000"/>
      <fCollisionDamageMult value="0.000000"/>
      <fWeaponDamageMult value="0.000000"/>
      <fDeformationDamageMult value="0.000000"/>
      <fEngineDamageMult value="0.000000"/>
      <fPetrolTankVolume value="0.000000"/>
      <fOilVolume value="0.000000"/>
      <fPetrolConsumptionRate value="0.000000"/>
      <fSeatOffsetDistX value="0.000000"/>
      <fSeatOffsetDistY value="0.000000"/>
      <fSeatOffsetDistZ value="0.000000"/>
      <nMonetaryValue value="0"/>
      <strModelFlags>0</strModelFlags>
      <strHandlingFlags>0</strHandlingFlags>
      <strDamageFlags>0</strDamageFlags>
      <SubHandlingData>
        <Item type="CCarHandlingData">
          <fBackEndPopUpCarImpulseMult value="0.000000"/>
          <fBackEndPopUpBuildingImpulseMult value="0.000000"/>
          <fBackEndPopUpMaxDeltaSpeed value="0.000000"/>
          <fCamberFront value="0.000000"/>
          <fCamberRear value="0.000000"/>
          <fCastor value="0.000000"/>
          <fToeFront value="0.000000"/>
          <fToeRear value="0.000000"/>
          <fEngineResistance value="0.000000"/>
          <strAdvancedFlags>0</strAdvancedFlags>
        </Item>
      </SubHandlingData>
    </Item>`;

const BIKE_TEMPLATE = `    <Item type="CHandlingData">
      <handlingName>EXAMPLE</handlingName>
      <fMass value="0.000000"/>
      <fInitialDragCoeff value="0.000000"/>
      <fDownforceModifier value="0.000000"/>
      <fPopUpLightRotation value="0.000000"/>
      <fPercentSubmerged value="0.000000"/>
      <vecCentreOfMassOffset x="0.000000" y="0.000000" z="0.000000"/>
      <vecInertiaMultiplier x="0.000000" y="0.000000" z="0.000000"/>
      <fDriveBiasFront value="0.000000"/>
      <nInitialDriveGears value="0"/>
      <fInitialDriveForce value="0.000000"/>
      <fDriveInertia value="0.000000"/>
      <fClutchChangeRateScaleUpShift value="0.000000"/>
      <fClutchChangeRateScaleDownShift value="0.000000"/>
      <fInitialDriveMaxFlatVel value="0.000000"/>
      <fBrakeForce value="0.000000"/>
      <fBrakeBiasFront value="0.000000"/>
      <fHandBrakeForce value="0.000000"/>
      <fSteeringLock value="0.000000"/>
      <fTractionCurveMin value="0.000000"/>
      <fTractionCurveMax value="0.000000"/>
      <fTractionCurveLateral value="0.000000"/>
      <fTractionSpringDeltaMax value="0.000000"/>
      <fLowSpeedTractionLossMult value="0.000000"/>
      <fCamberStiffnesss value="0.000000"/>
      <fTractionBiasFront value="0.000000"/>
      <fTractionLossMult value="0.000000"/>
      <fSuspensionForce value="0.000000"/>
      <fSuspensionCompDamp value="0.000000"/>
      <fSuspensionReboundDamp value="0.000000"/>
      <fSuspensionUpperLimit value="0.000000"/>
      <fSuspensionLowerLimit value="0.000000"/>
      <fSuspensionRaise value="0.000000"/>
      <fSuspensionBiasFront value="0.000000"/>
      <fAntiRollBarForce value="0.000000"/>
      <fAntiRollBarBiasFront value="0.000000"/>
      <fRollCentreHeightFront value="0.000000"/>
      <fRollCentreHeightRear value="0.000000"/>
      <fCollisionDamageMult value="0.000000"/>
      <fWeaponDamageMult value="0.000000"/>
      <fDeformationDamageMult value="0.000000"/>
      <fEngineDamageMult value="0.000000"/>
      <fPetrolTankVolume value="0.000000"/>
      <fOilVolume value="0.000000"/>
      <fPetrolConsumptionRate value="0.000000"/>
      <fSeatOffsetDistX value="0.000000"/>
      <fSeatOffsetDistY value="0.000000"/>
      <fSeatOffsetDistZ value="0.000000"/>
      <nMonetaryValue value="0"/>
      <strModelFlags>0</strModelFlags>
      <strHandlingFlags>0</strHandlingFlags>
      <strDamageFlags>0</strDamageFlags>
      <AIHandling>AVERAGE</AIHandling>
      <SubHandlingData>
        <Item type="CBikeHandlingData">
          <fLeanFwdCOMMult value="0.000000"/>
          <fLeanFwdForceMult value="0.000000"/>
          <fLeanBakCOMMult value="0.000000"/>
          <fLeanBakForceMult value="0.000000"/>
          <fMaxBankAngle value="0.000000"/>
          <fFullAnimAngle value="0.000000"/>
          <fDesLeanReturnFrac value="0.000000"/>
          <fStickLeanMult value="0.000000"/>
          <fBrakingStabilityMult value="0.000000"/>
          <fInAirSteerMult value="0.000000"/>
          <fWheelieBalancePoint value="0.000000"/>
          <fStoppieBalancePoint value="0.000000"/>
          <fWheelieSteerMult value="0.000000"/>
          <fRearBalanceMult value="0.000000"/>
          <fFrontBalanceMult value="0.000000"/>
          <fBikeGroundSideFrictionMult value="0.000000"/>
          <fBikeWheelGroundSideFrictionMult value="0.000000"/>
          <fBikeOnStandLeanAngle value="0.000000"/>
          <fBikeOnStandSteerAngle value="0.000000"/>
          <fJumpForce value="0.000000"/>
        </Item>
      </SubHandlingData>
    </Item>`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isBikeClass(vc: VehicleClass) {
  return vc.startsWith('Bike-');
}

/** Extracts just the <Item type="CHandlingData">...</Item> block from a full XML */
function extractItem(xml: string): string {
  const start = xml.indexOf('<Item type="CHandlingData">');
  if (start === -1) return xml;
  const lastClose = xml.lastIndexOf('</Item>');
  if (lastClose === -1) return xml;
  return xml.substring(start, lastClose + '</Item>'.length);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Handling() {
  const qc = useQueryClient();
  const mainRef = useRef<HTMLDivElement>(null);

  const [search, setSearch]             = useState('');
  const [selectedId, setSelectedId]     = useState<number | null>(null);
  const [xmlInput, setXmlInput]         = useState('');
  const [changeNote, setChangeNote]     = useState('');
  const [activeTab, setActiveTab]       = useState<'current' | 'history'>('current');
  const [expandedRevId, setExpandedRevId] = useState<number | null>(null);
  const [copyFeedback, setCopyFeedback]   = useState<'full' | 'item' | null>(null);
  const [shakeNote, setShakeNote]         = useState(false);

  // ── GSAP ─────────────────────────────────────────────────────────
  useGSAP(() => {
    if (mainRef.current) {
      gsap.from(mainRef.current, { opacity: 0, y: 10, duration: 0.3, ease: 'power2.out' });
    }
  }, [selectedId]);

  // ── Data ─────────────────────────────────────────────────────────
  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles', {}],
    queryFn: () => vehiclesApi.list({}),
  });

  const { data: revisionsData } = useQuery({
    queryKey: ['vehicle-handling', selectedId],
    queryFn: () => vehicleHandlingApi.list(selectedId!),
    enabled: selectedId !== null,
  });

  const saveMutation = useMutation({
    mutationFn: (data: { handling_xml: string; change_note: string }) =>
      vehicleHandlingApi.create(selectedId!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicle-handling', selectedId] });
      setXmlInput('');
      setChangeNote('');
      setActiveTab('current');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (revId: number) => vehicleHandlingApi.delete(selectedId!, revId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicle-handling', selectedId] }),
  });

  // ── Derived ──────────────────────────────────────────────────────
  const allVehicles = vehiclesData?.vehicles ?? [];

  // Only show vehicles actively being worked on — hide Todo, Approved, Pushed, Merged
  const ACTIVE_STATUSES: string[] = ['In Progress / Active Editing', 'In Review'];

  const filtered = allVehicles
    .filter(v => ACTIVE_STATUSES.includes(v.progress))
    .filter(v =>
      !search ||
      v.name?.toLowerCase().includes(search.toLowerCase()) ||
      v.spawncode.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => (a.name ?? a.spawncode).localeCompare(b.name ?? b.spawncode));

  const selectedVehicle = allVehicles.find(v => v.id === selectedId) ?? null;
  const revisions = revisionsData?.revisions ?? [];
  const latestRevision = revisions[0] ?? null;

  // ── Actions ──────────────────────────────────────────────────────
  const handleCopy = (text: string, type: 'full' | 'item' = 'full') => {
    navigator.clipboard.writeText(text);
    setCopyFeedback(type);
    setTimeout(() => setCopyFeedback(null), 1500);
  };

  const handleSubmit = () => {
    if (!xmlInput.trim()) return;
    if (!changeNote.trim()) {
      setShakeNote(true);
      setTimeout(() => setShakeNote(false), 500);
      return;
    }
    saveMutation.mutate({ handling_xml: xmlInput.trim(), change_note: changeNote.trim() });
  };

  const loadTemplate = (type: 'car' | 'bike') => {
    setXmlInput(type === 'bike' ? BIKE_TEMPLATE : CAR_TEMPLATE);
  };

  const selectVehicle = (v: Vehicle) => {
    setSelectedId(v.id);
    setXmlInput('');
    setChangeNote('');
    setActiveTab('current');
    setExpandedRevId(null);
  };

  return (
    <div className="flex min-h-screen sakura-bg" style={{ height: 'calc(100vh - 57px)' }}>

      {/* ── Left: vehicle picker ──────────────────────────────────── */}
      <div
        className="flex flex-col flex-shrink-0 border-r"
        style={{ width: 280, background: 'var(--panel)', borderColor: 'var(--border)' }}
      >
        {/* Panel header */}
        <div className="px-4 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <h2 className="font-heading text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>
            Select Vehicle
          </h2>
          <input
            className="input text-xs"
            placeholder="Search name or spawncode…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Vehicle list */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center space-y-1">
              <p className="font-body text-xs" style={{ color: 'var(--text-faint)' }}>
                {search ? 'No matches' : 'No active vehicles'}
              </p>
              {!search && (
                <p className="font-body text-[10px] leading-relaxed" style={{ color: 'var(--text-faint)' }}>
                  Move a vehicle to<br/>
                  <span style={{ color: 'var(--text-muted)' }}>In Progress</span> or{' '}
                  <span style={{ color: 'var(--text-muted)' }}>In Review</span><br/>
                  to see it here
                </p>
              )}
            </div>
          )}
          {filtered.map(v => {
            const col      = CLASS_COLOURS[v.vehicle_class] ?? { bg: '#374151', text: '#FFFFFF' };
            const progCol  = PROGRESS_COLOURS[v.progress];
            const isActive = v.id === selectedId;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => selectVehicle(v)}
                className="w-full text-left px-3 py-2.5 transition-colors flex items-start gap-2 border-b"
                style={{
                  background: isActive ? `${col.bg}18` : 'transparent',
                  borderColor: 'var(--border-subtle)',
                  borderLeft: isActive ? `3px solid ${col.bg}` : '3px solid transparent',
                }}
              >
                {/* Image thumbnail */}
                <div
                  className="w-10 h-7 flex-shrink-0 overflow-hidden flex items-center justify-center"
                  style={{ background: `${col.bg}20` }}
                >
                  {vehicleImageSrc(v) ? (
                    <img src={vehicleImageSrc(v)!} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={col.bg} strokeWidth="1.5" opacity={0.5}>
                      <rect x="2" y="7" width="20" height="12" rx="0"/>
                      <path d="M16 7l-2-4H10L8 7"/>
                      <circle cx="7" cy="16" r="2"/>
                      <circle cx="17" cy="16" r="2"/>
                    </svg>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className="font-heading text-[9px] font-bold uppercase tracking-wide px-1 flex-shrink-0"
                      style={{ background: col.bg, color: col.text }}
                    >
                      {classLabel(v.vehicle_class)}
                    </span>
                    <span
                      className="font-body text-[9px] truncate"
                      style={{ color: 'var(--text-faint)', letterSpacing: '0.03em' }}
                    >
                      {v.spawncode}
                    </span>
                  </div>
                  <p
                    className="font-heading text-xs truncate leading-tight"
                    style={{ color: 'var(--text)', fontWeight: isActive ? 700 : 500 }}
                  >
                    {v.name ?? v.spawncode}
                  </p>
                  <div className="mt-1">
                    <span
                      className="font-heading text-[8px] uppercase tracking-wide px-1 py-0"
                      style={{ background: progCol.bg, color: progCol.text }}
                    >
                      {progCol.label}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Bottom count */}
        <div className="px-4 py-2 border-t flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <p className="font-body text-[10px]" style={{ color: 'var(--text-faint)' }}>
            {filtered.length} active vehicle{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* ── Right: workspace ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {!selectedVehicle ? (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" opacity={0.25}>
              <rect x="4" y="16" width="40" height="22" rx="0" stroke="currentColor" strokeWidth="2"/>
              <path d="M32 16l-4-8H20l-4 8" stroke="currentColor" strokeWidth="2"/>
              <circle cx="14" cy="30" r="4" stroke="currentColor" strokeWidth="2"/>
              <circle cx="34" cy="30" r="4" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <div className="text-center">
              <p className="font-heading text-xl uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>
                Select a Vehicle
              </p>
              <p className="font-body text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                Choose a vehicle from the left to view or submit handling data
              </p>
            </div>
          </div>
        ) : (
          <div ref={mainRef} className="flex-1 flex flex-col overflow-hidden">

            {/* ── Vehicle header ──────────────────────────────────── */}
            {(() => {
              const col     = CLASS_COLOURS[selectedVehicle.vehicle_class] ?? { bg: '#374151', text: '#FFFFFF' };
              const progCol = PROGRESS_COLOURS[selectedVehicle.progress];
              return (
                <div className="flex-shrink-0 border-b px-6 py-3 flex items-center gap-4" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
                  {/* Class badge */}
                  <span className="font-heading text-xs font-bold uppercase tracking-widest px-2.5 py-1" style={{ background: col.bg, color: col.text }}>
                    {classLabel(selectedVehicle.vehicle_class)}
                  </span>

                  {/* Name */}
                  <h1 className="font-heading text-lg font-bold tracking-wide flex-1 min-w-0 truncate" style={{ color: 'var(--text)' }}>
                    {selectedVehicle.name ?? selectedVehicle.spawncode}
                  </h1>

                  {/* Spawncode */}
                  <span className="font-body text-xs hidden sm:block" style={{ color: 'var(--text-muted)' }}>
                    {selectedVehicle.spawncode}
                  </span>

                  {/* Status */}
                  <span className="font-heading text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" style={{ background: progCol.bg, color: progCol.text }}>
                    {progCol.label}
                  </span>

                  {/* Revision count */}
                  <span className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
                    {revisions.length} revision{revisions.length !== 1 ? 's' : ''}
                  </span>
                </div>
              );
            })()}

            {/* ── Tabs ─────────────────────────────────────────────── */}
            <div className="flex border-b flex-shrink-0" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
              {(['current', 'history'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="px-5 py-2.5 font-heading uppercase text-xs tracking-wide border-b-2 transition-colors"
                  style={activeTab === tab
                    ? { borderColor: '#C8102E', color: '#C8102E' }
                    : { borderColor: 'transparent', color: 'var(--text-muted)' }
                  }
                >
                  {tab === 'current' ? 'Current & Submit' : `History (${revisions.length})`}
                </button>
              ))}
            </div>

            {/* ── Tab content ──────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">

              {/* ─── CURRENT + SUBMIT TAB ────────────────────────── */}
              {activeTab === 'current' && (
                <>
                  {/* Current latest XML */}
                  {latestRevision ? (
                    <div className="border" style={{ borderColor: 'var(--border)' }}>
                      <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                        <div className="flex items-center gap-3">
                          <span className="font-heading text-xs uppercase tracking-wide" style={{ color: 'var(--text)' }}>
                            Latest Revision
                          </span>
                          <span className="font-body text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            by <strong style={{ color: 'var(--text)' }}>{latestRevision.changed_by}</strong>
                            {' · '}{formatDate(latestRevision.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleCopy(extractItem(latestRevision.handling_xml), 'item')}
                            className="font-heading text-[10px] uppercase tracking-wide px-3 py-1 border transition-colors hover:text-primary"
                            style={{ borderColor: 'var(--border-strong)', color: copyFeedback === 'item' ? '#C8102E' : 'var(--text-muted)' }}
                          >
                            {copyFeedback === 'item' ? '✓ Copied!' : 'Copy Item'}
                          </button>
                          <button
                            onClick={() => handleCopy(latestRevision.handling_xml, 'full')}
                            className="font-heading text-[10px] uppercase tracking-wide px-3 py-1 border transition-colors hover:text-primary"
                            style={{ borderColor: 'var(--border-strong)', color: copyFeedback === 'full' ? '#C8102E' : 'var(--text-muted)' }}
                          >
                            {copyFeedback === 'full' ? '✓ Copied!' : 'Copy Full'}
                          </button>
                        </div>
                      </div>
                      {latestRevision.change_note && (
                        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border)', background: `#C8102E08` }}>
                          <p className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
                            <span className="font-heading uppercase text-[9px] tracking-wide mr-2" style={{ color: '#C8102E' }}>Note</span>
                            {latestRevision.change_note}
                          </p>
                        </div>
                      )}
                      <pre
                        className="font-body text-[11px] p-4 overflow-x-auto"
                        style={{
                          background: 'var(--surface)',
                          lineHeight: 1.6,
                          maxHeight: 300,
                          overflowY: 'auto',
                          whiteSpace: 'pre',
                        }}
                      >
                        <XmlHighlight xml={latestRevision.handling_xml} />
                      </pre>
                    </div>
                  ) : (
                    <div className="border px-4 py-6 text-center" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                      <p className="font-heading text-sm uppercase tracking-wide" style={{ color: 'var(--text-faint)' }}>No handling data yet</p>
                      <p className="font-body text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Submit the first revision below</p>
                    </div>
                  )}

                  {/* ── Submit new revision form ──────────────────── */}
                  <div className="border" style={{ borderColor: 'var(--border)' }}>
                    {/* Form header */}
                    <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                      <span className="font-heading text-xs uppercase tracking-wide" style={{ color: 'var(--text)' }}>
                        Submit New Revision
                      </span>
                      {/* Template buttons */}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => loadTemplate(isBikeClass(selectedVehicle.vehicle_class) ? 'bike' : 'car')}
                          className="font-heading text-[10px] uppercase tracking-wide px-2.5 py-1 border transition-colors hover:text-primary"
                          style={{ borderColor: 'var(--border-strong)', color: 'var(--text-muted)' }}
                        >
                          Load {isBikeClass(selectedVehicle.vehicle_class) ? 'Bike' : 'Car'} Template
                        </button>
                        {!isBikeClass(selectedVehicle.vehicle_class) && (
                          <button
                            type="button"
                            onClick={() => loadTemplate('bike')}
                            className="font-heading text-[10px] uppercase tracking-wide px-2.5 py-1 border transition-colors hover:text-primary"
                            style={{ borderColor: 'var(--border-strong)', color: 'var(--text-muted)' }}
                          >
                            Load Bike Template
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="p-4 space-y-4">
                      {/* XML input */}
                      <div>
                        <label className="label">Handling XML *</label>
                        <textarea
                          value={xmlInput}
                          onChange={e => setXmlInput(e.target.value)}
                          placeholder={`Paste handling XML here…\n\n    <Item type="CHandlingData">\n      <handlingName>YOURCAR</handlingName>\n      …\n    </Item>`}
                          className="input resize-none"
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 11,
                            lineHeight: 1.6,
                            height: 240,
                            background: 'var(--surface)',
                            color: 'var(--text)',
                          }}
                        />
                      </div>

                      {/* Change note */}
                      <div className={shakeNote ? 'shake' : ''}>
                        <label className="label">
                          Change Notes *
                          <span className="ml-2 font-body normal-case tracking-normal text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            (required — describe what was changed and why)
                          </span>
                        </label>
                        <textarea
                          value={changeNote}
                          onChange={e => setChangeNote(e.target.value)}
                          placeholder="e.g. Adjusted fDriveBiasFront from 0.4 → 0.45 for better corner turn-in. Reduced fTractionLossMult to improve high-speed grip."
                          className="input resize-none"
                          style={{ height: 80, color: 'var(--text)' }}
                        />
                      </div>

                      {/* Submit */}
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={saveMutation.isPending || !xmlInput.trim()}
                          className="px-5 py-2 bg-dark text-white text-sm font-heading uppercase hover:bg-primary transition-colors disabled:opacity-40"
                        >
                          {saveMutation.isPending ? 'Saving…' : 'Submit Revision'}
                        </button>
                        {(xmlInput || changeNote) && (
                          <button
                            type="button"
                            onClick={() => { setXmlInput(''); setChangeNote(''); }}
                            className="px-4 py-2 border text-xs font-heading uppercase transition-colors"
                            style={{ borderColor: 'var(--border-strong)', color: 'var(--text-muted)' }}
                          >
                            Clear
                          </button>
                        )}
                        {!changeNote.trim() && xmlInput.trim() && (
                          <span className="font-body text-xs text-primary">⚠ Change notes are required</span>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ─── HISTORY TAB ─────────────────────────────────── */}
              {activeTab === 'history' && (
                <>
                  {revisions.length === 0 ? (
                    <div className="border px-4 py-10 text-center" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                      <p className="font-heading text-sm uppercase tracking-wide" style={{ color: 'var(--text-faint)' }}>No revisions yet</p>
                      <p className="font-body text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Submit the first handling revision on the Current tab</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {revisions.map((rev, i) => (
                        <RevisionCard
                          key={rev.id}
                          revision={rev}
                          isLatest={i === 0}
                          isExpanded={expandedRevId === rev.id}
                          onToggle={() => setExpandedRevId(expandedRevId === rev.id ? null : rev.id)}
                          onCopyFull={() => handleCopy(rev.handling_xml, 'full')}
                          onCopyItem={() => handleCopy(extractItem(rev.handling_xml), 'item')}
                          onDelete={() => {
                            if (window.confirm('Delete this revision? This cannot be undone.')) {
                              deleteMutation.mutate(rev.id);
                            }
                          }}
                          copyFeedback={copyFeedback}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Revision Card ────────────────────────────────────────────────────────────

interface RevisionCardProps {
  revision: HandlingRevision;
  isLatest: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onCopyFull: () => void;
  onCopyItem: () => void;
  onDelete: () => void;
  copyFeedback: 'full' | 'item' | null;
}

function RevisionCard({ revision, isLatest, isExpanded, onToggle, onCopyFull, onCopyItem, onDelete, copyFeedback }: RevisionCardProps) {
  return (
    <div className="border" style={{ borderColor: 'var(--border)', borderLeft: isLatest ? '3px solid #C8102E' : '3px solid var(--border)' }}>
      {/* Header row */}
      <div className="px-4 py-2.5 flex items-start gap-3" style={{ background: 'var(--surface)' }}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isLatest && (
              <span className="font-heading text-[9px] uppercase tracking-wide px-1.5 py-0.5 bg-primary text-white">
                Latest
              </span>
            )}
            <span className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
              by <strong style={{ color: 'var(--text)' }}>{revision.changed_by}</strong>
              {' · '}{formatDate(revision.created_at)}
            </span>
          </div>
          {revision.change_note && (
            <p className="font-body text-xs mt-1.5 leading-relaxed" style={{ color: 'var(--text)' }}>
              <span className="font-heading text-[9px] uppercase tracking-wide mr-1.5" style={{ color: '#C8102E' }}>Note:</span>
              {revision.change_note}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onCopyItem}
            className="font-heading text-[10px] uppercase tracking-wide px-2.5 py-1 border transition-colors hover:text-primary"
            style={{ borderColor: 'var(--border-strong)', color: copyFeedback === 'item' ? '#C8102E' : 'var(--text-muted)' }}
          >
            {copyFeedback === 'item' ? '✓ Copied!' : 'Copy Item'}
          </button>
          <button
            onClick={onCopyFull}
            className="font-heading text-[10px] uppercase tracking-wide px-2.5 py-1 border transition-colors hover:text-primary"
            style={{ borderColor: 'var(--border-strong)', color: copyFeedback === 'full' ? '#C8102E' : 'var(--text-muted)' }}
          >
            {copyFeedback === 'full' ? '✓ Copied!' : 'Copy Full'}
          </button>
          <button
            onClick={onToggle}
            className="font-heading text-[10px] uppercase tracking-wide px-2.5 py-1 border transition-colors hover:text-primary"
            style={{ borderColor: 'var(--border-strong)', color: 'var(--text-muted)' }}
          >
            {isExpanded ? 'Hide XML' : 'View XML'}
          </button>
          <button
            onClick={onDelete}
            className="font-heading text-[10px] uppercase tracking-wide px-2.5 py-1 border transition-colors hover:bg-primary hover:text-white hover:border-primary"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
          >
            ×
          </button>
        </div>
      </div>

      {/* XML block — collapsible */}
      {isExpanded && (
        <pre
          className="font-body text-[11px] p-4 border-t overflow-x-auto"
          style={{
            background: 'var(--card)',
            borderColor: 'var(--border)',
            lineHeight: 1.6,
            maxHeight: 400,
            overflowY: 'auto',
            whiteSpace: 'pre',
          }}
        >
          <XmlHighlight xml={revision.handling_xml} />
        </pre>
      )}
    </div>
  );
}
